/**
 * USER DOCS: changes to this module should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/index.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting)
 */

/**
 * Digital Voting — client-side API helpers for the management view.
 * All calls go through /api/v1/voting/* with the portal JWT (apiFetch).
 */

import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/apiFetch';
import type {
  VotingItem,
  VotingItemStatus,
  VotingItemType,
  VotingSession,
  VotingSessionStatus,
  VotingTally,
  VotingThreshold,
  VotingVoter,
} from '@/lib/voting/types';
import type { BallotSourceForm } from '@/lib/voting/ballotFormAdapter';

export type { VotingItem, VotingSession, VotingThreshold, VotingVoter, VotingTally };
export type { VotingSessionStatus, VotingItemStatus, VotingItemType };

export interface VotingSessionSummary extends VotingSession {
  item_count: number;
}

export interface VotingSessionDetail {
  session: VotingSession;
  items: VotingItem[];
  quorum: { eligible: number; present: number; requiredPct: number | null; met: boolean | null } | null;
  voter_count: number;
  /** Server-side manage check (host/creator/owner/ops admin) — drives edit UI. */
  can_manage: boolean;
  /** Path-only share link for the read-only projector display. */
  projector_url?: string;
}

export interface VotingLaunchResult {
  session: VotingSession;
  scheduled?: boolean;
  meeting_id?: string;
  invitation_token?: string;
  join_url?: string;
  host_join_url?: string;
  /** Read-only results display for the meeting-room projector/secretary. */
  display_url?: string;
  vote_url_base?: string;
  opened_items?: number[];
}

export interface VoterToken {
  voter_key: string;
  token: string;
  vote_url: string;
}

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json as T;
}

// ── Sessions ────────────────────────────────────────────────────────────────

export async function listSessions(params?: {
  status?: string;
  search?: string;
  limit?: number;
  location_id?: number;
}): Promise<VotingSessionSummary[]> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.search) q.set('search', params.search);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.location_id != null) q.set('location_id', String(params.location_id));
  const json = await unwrap<{ sessions: VotingSessionSummary[] }>(
    await apiGet(`/api/v1/voting/sessions${q.size ? `?${q}` : ''}`),
  );
  return json.sessions;
}

export interface CreateSessionInput {
  title: string;
  description?: string;
  session_type?: string;
  delivery_mode?: 'meeting' | 'async';
  scheduled_start?: string | null;
  voting_opens_at?: string | null;
  voting_closes_at?: string | null;
  quorum_required?: boolean;
  quorum_percent?: number | null;
  settings?: Record<string, unknown>;
  meeting_id?: string;
  /** Estate/property (contact_locations.id) this vote belongs to. */
  location_id?: number | null;
}

export async function createSession(input: CreateSessionInput): Promise<VotingSession> {
  const json = await unwrap<{ session: VotingSession }>(
    await apiPost('/api/v1/voting/sessions', input),
  );
  return json.session;
}

export async function getSessionDetail(id: string): Promise<VotingSessionDetail> {
  return unwrap<VotingSessionDetail>(await apiGet(`/api/v1/voting/sessions/${id}`));
}

export async function updateSession(
  id: string,
  patch: Partial<CreateSessionInput>,
): Promise<VotingSession> {
  const json = await unwrap<{ session: VotingSession }>(
    await apiPatch(`/api/v1/voting/sessions/${id}`, patch),
  );
  return json.session;
}

export async function deleteSession(id: string): Promise<void> {
  await unwrap(await apiDelete(`/api/v1/voting/sessions/${id}`));
}

export async function launchSession(id: string): Promise<VotingLaunchResult> {
  return unwrap<VotingLaunchResult>(await apiPost(`/api/v1/voting/sessions/${id}/launch`, {}));
}

export async function openSession(id: string): Promise<VotingSession> {
  const json = await unwrap<{ session: VotingSession }>(
    await apiPost(`/api/v1/voting/sessions/${id}/open`, {}),
  );
  return json.session;
}

export async function closeSession(id: string): Promise<VotingSession> {
  const json = await unwrap<{ session: VotingSession }>(
    await apiPost(`/api/v1/voting/sessions/${id}/close`, {}),
  );
  return json.session;
}

// ── Items ───────────────────────────────────────────────────────────────────

export interface ItemInput {
  title: string;
  description?: string;
  item_type?: VotingItemType;
  options: { key: string; label: string; description?: string }[];
  max_choices?: number;
  timer_seconds?: number | null;
  threshold?: VotingThreshold;
  /** Provenance when the item was imported from a designed ballot form. */
  source_form_id?: number | null;
  source_form_key?: string | null;
}

export async function createItem(sessionId: string, item: ItemInput): Promise<VotingItem[]> {
  const json = await unwrap<{ items: VotingItem[] }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/items`, { item }),
  );
  return json.items;
}

export async function createItems(sessionId: string, items: ItemInput[]): Promise<VotingItem[]> {
  const json = await unwrap<{ items: VotingItem[] }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/items`, { items }),
  );
  return json.items;
}

export interface AgendaParseResponse {
  items: ItemInput[];
  warnings: string[];
  format: string | null;
  source: 'heuristic' | 'ai' | 'none';
}

/**
 * Parse an uploaded agenda/ballot document (DOCX / PDF / ODT / TXT) or pasted
 * text into candidate voting items — review flow: the caller edits the result
 * and posts the confirmed set through createItems.
 */
export async function parseAgendaDocument(
  sessionId: string,
  opts: { file_name?: string; file_data?: string; text?: string; mode?: 'auto' | 'heuristic' | 'ai' },
): Promise<AgendaParseResponse> {
  return unwrap<AgendaParseResponse>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/agenda`, opts),
  );
}

export async function updateItem(id: number, patch: Partial<ItemInput>): Promise<VotingItem> {
  const json = await unwrap<{ item: VotingItem }>(
    await apiPatch(`/api/v1/voting/items/${id}`, patch),
  );
  return json.item;
}

export async function deleteItem(id: number): Promise<void> {
  await unwrap(await apiDelete(`/api/v1/voting/items/${id}`));
}

// ── Ballot authoring (Total Forms integration) ─────────────────────────────

export interface BallotFormSummary {
  id: number;
  form_key: string;
  title: string;
  description?: string;
  form_purpose?: string;
  form_schema?: BallotSourceForm['form_schema'];
  updated_at?: string;
}

/**
 * Forms authored as 'ballot' (Digital Voting) or 'poll' in the unified
 * designer, with their schemas — converted to agenda items client-side by
 * ballotFormAdapter so the import is a one-time snapshot.
 */
export async function listBallotForms(): Promise<BallotFormSummary[]> {
  const json = await unwrap<{ forms: BallotFormSummary[] }>(
    await apiGet('/api/v1/forms/config?form_purpose=ballot,poll&include_schema=true'),
  );
  return Array.isArray(json.forms) ? json.forms : [];
}

export async function reorderItems(sessionId: string, order: number[]): Promise<VotingItem[]> {
  const json = await unwrap<{ items: VotingItem[] }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/items/reorder`, { order }),
  );
  return json.items;
}

export async function showItem(id: number): Promise<VotingItem> {
  const json = await unwrap<{ item: VotingItem }>(await apiPost(`/api/v1/voting/items/${id}/show`, {}));
  return json.item;
}

export async function openItem(id: number, timerSeconds?: number): Promise<VotingItem> {
  const json = await unwrap<{ item: VotingItem }>(
    await apiPost(`/api/v1/voting/items/${id}/open`, timerSeconds ? { timer_seconds: timerSeconds } : {}),
  );
  return json.item;
}

export async function extendItem(id: number, addSeconds: number): Promise<VotingItem> {
  const json = await unwrap<{ item: VotingItem }>(
    await apiPost(`/api/v1/voting/items/${id}/extend`, { add_seconds: addSeconds }),
  );
  return json.item;
}

export async function closeItem(id: number): Promise<VotingItem> {
  const json = await unwrap<{ item: VotingItem }>(
    await apiPost(`/api/v1/voting/items/${id}/close`, {}),
  );
  return json.item;
}

export interface ItemTallyResult {
  item: VotingItem;
  cast: number;
  eligible: number;
}

export async function getItemTally(id: number): Promise<ItemTallyResult> {
  return unwrap<ItemTallyResult>(await apiGet(`/api/v1/voting/items/${id}/tally`));
}

// ── Roll ────────────────────────────────────────────────────────────────────

export interface VoterInput {
  voter_key?: string;
  user_id?: number;
  display_name?: string;
  member_ref?: string;
  weight?: number;
  eligible?: boolean;
  proxy_of?: string;
}

export async function listVoters(sessionId: string): Promise<VotingVoter[]> {
  const json = await unwrap<{ voters: VotingVoter[] }>(
    await apiGet(`/api/v1/voting/sessions/${sessionId}/voters`),
  );
  return json.voters;
}

export async function upsertVoters(sessionId: string, voters: VoterInput[]): Promise<number> {
  const json = await unwrap<{ count: number }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/voters`, { voters }),
  );
  return json.count;
}

export interface ResidentVoterPreview {
  person_id: number;
  voter_key: string;
  display_name: string;
  member_ref: string;
  user_id: number | null;
  has_login: boolean;
  role_at_location: string | null;
  portal_role: string | null;
  /** null for stakeholder-matched people with no unit link. */
  location_id: number | null;
  location_name: string;
  account_status: string | null;
  current_balance: number | null;
  in_arrears: boolean;
  qualifies: boolean;
  /** Requested audience-segment roles this person matched. */
  matched_roles: string[];
  /** Registered contact channels — /vote/identify OTP self-serve needs one. */
  has_email: boolean;
  has_phone: boolean;
  has_whatsapp: boolean;
  /** Portal login or at least one OTP-reachable channel. */
  verifiable: boolean;
  already_on_roll: boolean;
}

export interface ResidentRollPreview {
  residents: ResidentVoterPreview[];
  defaults: {
    roles: string[];
    segments?: string[];
    weight_field?: string;
    arrears?: 'ineligible' | 'exclude' | 'ignore';
  };
  counts: {
    total: number;
    qualifying: number;
    in_arrears: number;
    with_login: number;
    verifiable: number;
    missing_contact: number;
  };
}

export async function previewResidentRoll(
  sessionId: string,
  opts?: { segments?: string[]; property_scope?: boolean },
): Promise<ResidentRollPreview> {
  const q = new URLSearchParams();
  if (opts?.segments?.length) q.set('segments', opts.segments.join(','));
  if (opts?.property_scope) q.set('property_scope', '1');
  return unwrap<ResidentRollPreview>(
    await apiGet(
      `/api/v1/voting/sessions/${sessionId}/voters/from-residents${q.size ? `?${q}` : ''}`,
    ),
  );
}

export async function importResidentRoll(
  sessionId: string,
  opts: {
    person_ids?: number[];
    /** Audience-segment roles — must match the preview's or stakeholder-only
     *  people silently drop out of the re-derived roll. */
    segments?: string[];
    /** Restrict the re-derived roll to the session's linked property scheme. */
    property_scope?: boolean;
    arrears?: 'ineligible' | 'exclude' | 'ignore';
  },
): Promise<{ imported: number; flagged_ineligible: number; skipped: number }> {
  return unwrap<{ imported: number; flagged_ineligible: number; skipped: number }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/voters/from-residents`, opts),
  );
}

// ── Electorate (Stakeholders tab) ──────────────────────────────────────────

/** One member of the client-wide voting electorate — the same derivation
 *  the session roll import uses, browsed independently of any session. */
export interface ElectorateMember {
  person_id: number;
  voter_key: string;
  display_name: string;
  member_ref: string;
  /** The person's own contact record — for the person editor. */
  contact_id: number | null;
  company_name: string | null;
  user_id: number | null;
  has_login: boolean;
  role_at_location: string | null;
  portal_role: string | null;
  location_id: number | null;
  location_name: string;
  account_status: string | null;
  current_balance: number | null;
  in_arrears: boolean;
  qualifies: boolean;
  matched_roles: string[];
  weight: number;
  has_email: boolean;
  has_phone: boolean;
  has_whatsapp: boolean;
  verifiable: boolean;
}

export interface ElectorateResult {
  members: ElectorateMember[];
  counts: {
    total: number;
    qualifying: number;
    in_arrears: number;
    with_login: number;
    verifiable: number;
    missing_contact: number;
  };
  /** Electorate size before the standing/verifiable/search filters. */
  electorate_total: number;
  /** Effective roll derivation config (yaml → client policy overrides). */
  roll_config?: {
    stakeholder_segments: string[];
    source_roles: string[];
    weight_field: string;
    arrears_policy: 'ineligible' | 'exclude' | 'ignore';
  };
}

export async function listElectorate(params?: {
  group_id?: number | null;
  segments?: string[];
  roles?: string[];
  standing?: 'up_to_date' | 'arrears';
  verifiable?: boolean;
  search?: string;
}): Promise<ElectorateResult> {
  const q = new URLSearchParams();
  if (params?.group_id != null) q.set('group_id', String(params.group_id));
  if (params?.segments?.length) q.set('segments', params.segments.join(','));
  if (params?.roles?.length) q.set('roles', params.roles.join(','));
  if (params?.standing) q.set('standing', params.standing);
  if (params?.verifiable) q.set('verifiable', '1');
  if (params?.search) q.set('search', params.search);
  return unwrap<ElectorateResult>(
    await apiGet(`/api/v1/voting/electorate${q.size ? `?${q}` : ''}`),
  );
}

export async function issueVoterTokens(
  sessionId: string,
  voterKeys?: string[],
): Promise<VoterToken[]> {
  const json = await unwrap<{ tokens: VoterToken[] }>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/voters/tokens`, {
      voter_keys: voterKeys,
    }),
  );
  return json.tokens;
}

export interface SessionInviteResult {
  invited: number;
  sent: { voter_key: string; email: string }[];
  failed: { voter_key: string; email: string; error: string }[];
  skipped: { voter_key: string; display_name: string | null; reason: string }[];
}

/**
 * Email every eligible roll member a calendar invite (METHOD:REQUEST .ics) so
 * a scheduled vote lands in their email-provider calendar. Members get a
 * direct ballot link; contact voters get the self-serve verify page.
 */
export async function sendSessionInvites(sessionId: string): Promise<SessionInviteResult> {
  return unwrap<SessionInviteResult>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/invites`, {}),
  );
}

export interface ArrearsNoticeResult {
  campaign_id: string;
  work_item_id: string | null;
  queued: number;
  recipients: { voter_key: string; email: string }[];
  skipped: { voter_key: string; display_name: string | null; reason: string }[];
  scheduled_at: string | null;
}

/**
 * Queue an arrears notice to every ineligible roll member through the
 * communications engine — registers as an 'Arrears Reminder' campaign work
 * item in Communications Management so the send is tracked like other
 * arrears correspondence.
 */
export async function sendArrearsNotice(
  sessionId: string,
  opts?: { subject?: string; message?: string; scheduled_at?: string },
): Promise<ArrearsNoticeResult> {
  return unwrap<ArrearsNoticeResult>(
    await apiPost(`/api/v1/voting/sessions/${sessionId}/arrears-notice`, opts ?? {}),
  );
}

// ── Results & minutes export ────────────────────────────────────────────────

export interface SessionItemReport {
  id: number;
  seq: number;
  item_type: VotingItemType;
  title: string;
  description: string | null;
  status: VotingItemStatus;
  outcome: string | null;
  results: { key: string; label: string; count: number; pct: number }[] | null;
  abstain: number | null;
  votes_cast: number | null;
}

export interface SessionResults {
  session: VotingSession & { anonymity?: string };
  items: SessionItemReport[];
  quorum: { eligible: number; present: number; requiredPct: number | null; met: boolean | null } | null;
  totals: {
    items: number;
    decided: number;
    votes_cast: number;
    passed: number;
    failed: number;
    tied: number;
    no_quorum: number;
  };
  generated_at: string;
}

export async function getSessionResults(id: string): Promise<SessionResults> {
  return unwrap<SessionResults>(await apiGet(`/api/v1/voting/sessions/${id}/results`));
}

/**
 * Download the minutes PDF or results CSV for a session. Auth'd fetch → blob
 * → anchor download (same convention as the SARS logbook export).
 */
export async function downloadSessionExport(
  id: string,
  format: 'pdf' | 'csv',
): Promise<void> {
  const res = await apiGet(`/api/v1/voting/sessions/${id}/results?format=${format}`);
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `voting-${id}.${format}`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Download the chronological audit trail CSV for a session. */
export async function downloadSessionAudit(id: string): Promise<void> {
  const res = await apiGet(`/api/v1/voting/sessions/${id}/audit?format=csv`);
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = match?.[1] ?? `voting-${id}-audit.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Persist minutes PDF + results/audit CSVs to the client's cloud drive. */
export async function archiveSessionArtifacts(
  id: string,
): Promise<Record<string, { id: string; name: string; path: string }>> {
  const json = await unwrap<{ files: Record<string, { id: string; name: string; path: string }> }>(
    await apiPost(`/api/v1/voting/sessions/${id}/archive`),
  );
  return json.files;
}

/** Seed (or fetch the existing) demo Estate AGM for evaluation. */
export async function createDemoSession(): Promise<VotingSession> {
  const json = await unwrap<{ session: VotingSession }>(
    await apiPost('/api/v1/voting/sessions/demo'),
  );
  return json.session;
}

// ── Dashboard summary ───────────────────────────────────────────────────────

export interface VotingSummary {
  sessions: { total: number; by_status: Record<string, number> };
  ballots_cast: number;
  voters: { eligible: number; present: number; turnout_pct: number | null };
  items_decided: number;
  outcomes: Record<string, number>;
  recent_closed: {
    id: string;
    title: string;
    session_type: string;
    closed_at: string | null;
  }[];
}

export async function getVotingSummary(): Promise<VotingSummary> {
  const { sessions, ballots_cast, voters, items_decided, outcomes, recent_closed } =
    await unwrap<VotingSummary & { success: boolean }>(
      await apiGet('/api/v1/voting/sessions/summary'),
    );
  return { sessions, ballots_cast, voters, items_decided, outcomes, recent_closed };
}

// ── Tenant policy ───────────────────────────────────────────────────────────

export interface VotingPolicyDefaults {
  anonymity: 'identified' | 'anonymous';
  weighted: boolean;
  change_vote_allowed: boolean;
  roll_required: boolean;
  results_visibility: 'everyone' | 'host_only' | 'after_close';
  majority_rule: 'simple' | 'two_thirds' | 'custom';
  custom_threshold_pct: number | null;
  default_timer_seconds: number;
  auto_advance: boolean;
  quorum_required: boolean;
  quorum_percent: number;
  member_ballot_tile: boolean;
  audit_digest_emails: string[];
  notification_settings: {
    invite_email: boolean;
    voter_otp_email: boolean;
    voter_otp_whatsapp: boolean;
    voter_otp_sms: boolean;
    audit_digest_email: boolean;
  };
  voter_roll: {
    source_roles: string[] | null;
    stakeholder_segments: string[] | null;
    weight_field: string | null;
    weight_fallback: number | null;
    arrears_policy: 'ineligible' | 'exclude' | 'ignore' | null;
  };
}

export async function getVotingPolicy(): Promise<VotingPolicyDefaults | null> {
  try {
    const json = await unwrap<{ success: boolean; data: VotingPolicyDefaults }>(
      await apiGet('/api/v1/voting/settings'),
    );
    return json.data ?? null;
  } catch {
    return null;
  }
}
